import has from 'lodash/has';
import filterOptions from '@enums/filter-options';
import agGridUtils from './ag-grid-utils';
import { getFilterValue } from './filter-util';
import { getTsgKeyFromColId } from './tsg-utils';

/**
 * Utility functions for managing grid view column state
 */

/**
 * Determines which tool store groups are allowed based on current filters and layout.
 *
 * @param {Object} filters - The retail attributes filter object
 * @param {Object} layout - The active layout object
 * @param {Object} storeGroupRelationships - Store group relationships from root state
 * @returns {Array} Array of allowed tool store group keys
 */
export const getAllowedToolStoreGroupKeys = (filters, layout, storeGroupRelationships) => {
  const toolStoreGroupValue = getFilterValue(filters, filterOptions.storegroupOption);
  const chainValue = getFilterValue(filters, filterOptions.chainOption);

  // Check if we have a valid tool store group filter (not null, undefined, or empty array)
  if (toolStoreGroupValue && Array.isArray(toolStoreGroupValue) && toolStoreGroupValue.length > 0) {
    // Direct tool store group filter
    return toolStoreGroupValue;
  }

  // Check if we have a valid chain filter (not null, undefined, or empty array)
  if (chainValue && Array.isArray(chainValue) && chainValue.length > 0) {
    // Chain filter - get tool store groups from the selected chains
    const selectedChains = storeGroupRelationships.storeGroupChains.filter(chain =>
      chainValue.includes(chain._id)
    );
    return selectedChains.flatMap(chain => chain.toolStoreGroupKeys);
  }

  // No filter applied - check if we have an active layout to determine allowed tool store groups
  if (layout && layout.columnState && layout.columnState.length > 0) {
    // Extract visible tool store group keys from the active layout's column state
    return agGridUtils.extractVisibleToolStoreGroupKeys(layout.columnState);
  }
  // No active layout or empty layout - all tool store groups are allowed
  return Object.keys(storeGroupRelationships.storegroupKeyNameMap);
};

/**
 * Builds a lookup map of layout hide state by column ID.
 *
 * @param {Object} layout - The active layout object
 * @returns {Object} Map of column ID to hide state
 */
export const buildLayoutHideLookup = layout => {
  const layoutHideByColId = {};
  if (layout && Array.isArray(layout.columnState)) {
    layout.columnState.forEach(col => {
      const id = col.colId || col.field;
      if (id) {
        layoutHideByColId[id] = col.hide === true;
      }
    });
  }
  return layoutHideByColId;
};

/**
 * Resolves competitor column visibility by combining layout and selection state.
 *
 * A saved layout may explicitly hide competitor columns. Those columns must stay hidden
 * when competitor visibility is recalculated after a global filter change.
 *
 * Old (legacy) layouts only stored visible columns (all with hide: false). When a column
 * is absent from such a layout, it was intentionally excluded and should remain hidden.
 * Modern layouts store ALL columns (visible with hide: false, hidden with hide: true),
 * so an explicit entry always exists. In both cases, absence from a non-empty layout
 * means the column should be hidden — unless the user explicitly made it visible via
 * the column chooser panel (tracked in visibleCompetitors).
 *
 * @param {string} columnIdentifier - The column identifier (colId or field)
 * @param {Array} selectedCompetitors - Array of selected competitor objects
 * @param {Set} visibleCompetitors - Set of visible competitor keys
 * @param {Object} layoutHideByColId - Layout hide state lookup
 * @returns {boolean} True if the competitor column should be hidden
 */
export const getCompetitorColumnHide = (
  columnIdentifier,
  selectedCompetitors,
  visibleCompetitors,
  layoutHideByColId = {}
) => {
  if (has(layoutHideByColId, columnIdentifier)) {
    return layoutHideByColId[columnIdentifier] === true;
  }

  // When a layout is active (non-empty lookup) but the column is not in it,
  // hide it unless the user explicitly made it visible in the column panel (visibleCompetitors).
  // Ignore global competitor selection (selectedCompetitors) — layout has priority.
  const layoutIsActive = Object.keys(layoutHideByColId).length > 0;
  if (layoutIsActive) {
    return agGridUtils.shouldHideCompetitorColumn(
      columnIdentifier,
      [], // Ignore globally selected — layout takes priority over global filter
      visibleCompetitors
    );
  }

  return agGridUtils.shouldHideCompetitorColumn(
    columnIdentifier,
    selectedCompetitors,
    visibleCompetitors
  );
};

/**
 * Processes a column based on tool store group filter rules.
 *
 * @param {Object} column - The column object to process
 * @param {Array} allowedToolStoreGroupKeys - Allowed TSG keys
 * @param {Object} layoutHideByColId - Layout hide state lookup
 * @param {Array} layoutVisibleToolStoreGroupKeys - Visible TSG keys in layout
 * @param {*} toolStoreGroupValue - Tool store group filter value
 * @param {*} chainValue - Chain filter value
 * @param {Object} state - The store state
 * @returns {Object} Processed column object
 */
export const processColumnForToolStoreGroupFilter = (
  column,
  allowedToolStoreGroupKeys,
  layoutHideByColId,
  layoutVisibleToolStoreGroupKeys,
  toolStoreGroupValue,
  chainValue,
  state
) => {
  const columnIdentifier = column.colId || column.field;

  // Handle tool store group columns
  if (columnIdentifier && columnIdentifier.includes('toolStoreGroups.')) {
    const tsgKey = getTsgKeyFromColId(columnIdentifier);

    if (tsgKey) {
      const isTsgAllowed = allowedToolStoreGroupKeys.includes(tsgKey);

      if (!isTsgAllowed) {
        // Tool store group is not allowed, hide the column
        return {
          ...column,
          hide: true,
        };
      }

      if (columnIdentifier.includes('competitors.')) {
        return {
          ...column,
          hide: getCompetitorColumnHide(
            columnIdentifier,
            state.selectedCompetitors,
            state.visibleCompetitors,
            layoutHideByColId
          ),
        };
      }

      // Non-competitor column within allowed TSG
      // Smart merge:
      // - If a TSG/chain filter is applied AND the TSG is NOT visible in the layout,
      //   force-show defaults (override layout): hide = false
      // - If no filter is applied OR the TSG is visible in the layout,
      //   respect the layout's existing hide state (return as-is)
      const filterApplied = !!toolStoreGroupValue || !!chainValue;
      const tsgInLayout = layoutVisibleToolStoreGroupKeys.includes(tsgKey);

      if (filterApplied && !tsgInLayout) {
        return {
          ...column,
          hide: false,
        };
      }

      // Respect the layout state from the active layout snapshot if present
      if (has(layoutHideByColId, columnIdentifier)) {
        return {
          ...column,
          hide: layoutHideByColId[columnIdentifier] === true,
        };
      }

      return column;
    }
  }

  // Handle simple competitor columns (not within tool store groups)
  if (
    columnIdentifier &&
    columnIdentifier.includes('competitors.') &&
    !columnIdentifier.includes('toolStoreGroups.')
  ) {
    return {
      ...column,
      hide: getCompetitorColumnHide(
        columnIdentifier,
        state.selectedCompetitors,
        state.visibleCompetitors,
        layoutHideByColId
      ),
    };
  }

  // Return unchanged column for non-tool store group and non-competitor columns
  return column;
};
